import {
  Box,
  ChevronUpIcon,
  Flex,
  JaneGoldIcon,
  Typography,
} from '@iheartjane/reefer';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { BloomGoldCalloutBanner } from '@jane/bloom/components/janeGold/bloomGoldCalloutBanner';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters/provider';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { JaneGoldDetailResults } from '@jane/bloom/loaders/routes/janeGoldDetail.server';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

import { GoldInfoBox } from './components/goldInfoBox';
import { GoldProductGrid } from './components/goldProductGrid';
import styles from './janeGoldDetailComponent.module.css';

export function JaneGoldDetailRouteComponent() {
  const { cart, pinnedStore } = useRootLoaderData();
  const {
    applicablePriceIds,
    availableFilters,
    brandSpecials,
    brandSpecialWithStatus,
    breadcrumbs,
    currentSpecial,
    customLanguage,
    hasLinkedBank,
    isSegmentedDeal,
    isSignedIn,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    recommendedSortResults,
    searchCriteria,
    sort: { currentSort, sortOptions },
    storeSpecials,
  } = useLoaderData<JaneGoldDetailResults>();

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  const mobileMiniTitle = `${currentSpecial?.title} · ${currentSpecial?.description}`;
  const mobileMiniSign =
    mobileMiniTitle.length > 50
      ? `${mobileMiniTitle.substring(0, 50)}...`
      : mobileMiniTitle;

  const [showMiniHeader, setShowMiniHeader] = useState(false);
  const infoBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerJdidToRum(jdid);
  }, [jdid]);

  useEffect(() => {
    const handleScroll = () => {
      if (infoBoxRef.current) {
        const rect = infoBoxRef.current.getBoundingClientRect();
        setShowMiniHeader(rect.bottom < 150);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isBundle = currentSpecial?.special_type !== 'product';

  return (
    <div className={styles['janeGoldDetailMain']}>
      <Box
        aria-label="Scroll to top"
        className={clsx(styles['janeGoldInfoBoxMini'], {
          [styles['visible']]: showMiniHeader,
        })}
        border="grays-light"
        borderRadius="lg"
        onClick={() => {
          window.scrollTo({ behavior: 'smooth', top: 0 });
        }}
      >
        <Flex
          flexDirection="row"
          className={styles['janeGoldInfoBoxMiniContent']}
        >
          <Flex className={styles['janeGoldMiniText']}>
            <JaneGoldIcon className="gold" style={{ marginRight: '8px' }} />
            <Flex flexDirection="column">
              <Typography color="grays-mid">{mobileMiniSign}</Typography>
              {!isSignedIn && isSegmentedDeal && (
                <Typography color="text-main" variant="body-bold">
                  Sign in for eligibility
                </Typography>
              )}
            </Flex>
          </Flex>
          <ChevronUpIcon color="text-main" />
        </Flex>
      </Box>
      <Breadcrumbs
        className={styles['janeGoldDetailBreadcrumbs']}
        breadcrumbs={breadcrumbs}
      />

      <BloomGoldCalloutBanner
        hasLinkedBank={hasLinkedBank}
        isSegmentedDeal={isSegmentedDeal}
        isSignedIn={isSignedIn}
        storeState={pinnedStore?.state}
        mb={16}
      />

      <div className={styles['janeGoldDetailContainer']}>
        <div ref={infoBoxRef}>
          <GoldInfoBox
            isBundle={isBundle}
            brand={currentSpecial?.product_brand.name}
            title={currentSpecial?.title}
            description={currentSpecial?.description ?? ''}
            terms={currentSpecial?.terms ?? ''}
            brandLogo={currentSpecial?.product_brand.logo_url ?? ''}
            tagLabel={currentSpecial?.discount_label ?? ''}
            brandSpecial={brandSpecialWithStatus}
          />
        </div>

        <BloomFilterProvider
          availableFilters={availableFilters}
          searchCriteria={searchCriteria}
        >
          <GoldProductGrid
            rowInstance={rowInstance}
            preloadedProductIds={menuProductImgsAboveTheFold.map(
              ({ id }) => id
            )}
            cart={cart}
            brandSpecials={brandSpecials}
            customLanguage={customLanguage}
            currentSort={currentSort}
            sortOptions={sortOptions}
            pinnedStore={pinnedStore || ({} as StoreViewModel)}
            janeGold={janeGold}
            storeSpecials={storeSpecials}
            applicablePriceIds={applicablePriceIds}
          />
        </BloomFilterProvider>
      </div>
    </div>
  );
}
